import React from "react";
import IndustryPage from "../../components/marketing/IndustryPage";
import { UtensilsCrossed, CalendarCheck, Clock, MessageSquare, Mic, FileText, Sparkles, ShoppingBag } from "lucide-react";

export default function Restaurants() {
  return (
    <IndustryPage
      slug="restaurants"
      industry="Restaurants"
      Icon={UtensilsCrossed}
      accent="#F59E0B"
      seoTitle="AI Reservation & Ordering Agent for Restaurants, Cafés & Hospitality"
      seoDescription="HelloSir.ai is the AI reservation and ordering agent for restaurants, cafés and hospitality groups. Takes bookings, answers menu & opening-hours questions, accepts dietary requirements and pushes orders into your POS — multilingual, on chat, voice and phone, 24/7."
      seoKeywords={[
        "AI restaurant chatbot", "AI reservation agent", "AI for cafes",
        "AI food ordering chatbot", "restaurant voice assistant",
        "AI booking agent for hospitality", "AI for hotels restaurants",
        "multilingual restaurant chatbot", "AI menu assistant", "AI for chain restaurants",
      ]}
      hero={{
        headline: "Hostess on duty 24/7 — never misses a booking again.",
        sub: "Take reservations, share menus, accommodate dietary needs and push orders into your POS — all over chat, the browser voice widget or a real phone call.",
      }}
      painPoints={[
        { Icon: Clock,         title: "Phone is always engaged", desc: "During service nobody can pick up — bookings and questions slip away." },
        { Icon: CalendarCheck, title: "Double-bookings & no-shows", desc: "Manual booking sheets create overlapping reservations and forgotten guests." },
        { Icon: MessageSquare, title: "Repetitive menu questions", desc: "\"Vegan options? Gluten free? Kids' menu? Is it spicy?\" — all night long." },
      ]}
      capabilities={[
        { Icon: CalendarCheck, title: "Takes reservations",        desc: "Real-time table availability with party size, dietary notes and special requests." },
        { Icon: UtensilsCrossed, title: "Menu expert",              desc: "Knows every dish, every allergen, every ingredient — trained on your live menu." },
        { Icon: ShoppingBag,   title: "Takes orders",                desc: "For delivery/pickup, pushes the order straight into your POS — Square, Toast, Lightspeed." },
        { Icon: Mic,           title: "Phone voice agent",           desc: "Picks up the restaurant line in a natural human voice. Books, answers, or transfers." },
        { Icon: FileText,      title: "Dietary & allergy aware",     desc: "Politely flags allergens and recommends safe alternatives from your menu." },
        { Icon: Sparkles,      title: "Upsells & specials",          desc: "Suggests today's special, paired wine, or chef's recommendation — increasing average ticket." },
      ]}
      sampleChats={[
        { q: "Table for 4 this Saturday at 8pm, one vegan.",
          a: "Saturday 8pm is fully booked. I have 7:30pm or 9:15pm. I've noted one vegan guest — our chef will prepare the cashew gnocchi. Which time works?" },
        { q: "Do you have gluten-free pasta?",
          a: "Yes — every pasta dish can be made with our gluten-free fusilli for £1.50 extra. The carbonara, arrabbiata and ravioli (with cheese filling) are all coeliac-friendly." },
        { q: "Pickup order: 2 pizzas, 1 tiramisu, in 30 minutes.",
          a: "Got it — 2× Margherita (£12 each), 1× tiramisu (£6.50), total £30.50. Pickup at 19:45. I'll text you when it's boxed. Payment in store or card link?" },
      ]}
      sources={[
        "Live booking system (OpenTable, ResDiary, custom)",
        "Live POS menu & prices",
        "Allergen & ingredient database",
        "Opening hours & holiday calendar",
      ]}
      integrations={[
        "OpenTable", "ResDiary", "SevenRooms", "Square POS", "Toast", "Lightspeed",
        "Stripe payment links", "WhatsApp Business", "Twilio SMS", "Google Calendar",
      ]}
      metrics={[
        { value: "+29%", label: "Reservations captured outside service" },
        { value: "−40%", label: "No-show rate (auto-reminders)" },
        { value: "24/7", label: "Multilingual coverage" },
        { value: "8s",   label: "Avg reply time" },
      ]}
      faqs={[
        { q: "Does the AI know my menu inside-out?",
          a: "Yes. Once you upload your menu (PDF or via POS sync), the agent knows every dish, every allergen and every price. Updates propagate instantly." },
        { q: "Can it handle phone reservations?",
          a: "Yes — through our VAPI/Twilio integration the agent answers your restaurant line in a natural voice and books tables in real time." },
        { q: "Will it suggest specials?",
          a: "Daily specials, pairings and chef's recommendations — all configurable per service or per day of the week." },
        { q: "Does it work for hotel restaurants and chains?",
          a: "Yes. Multi-location restaurants get a separate agent per branch with location-aware menus and calendars." },
      ]}
    />
  );
}
